import moment from "moment";
import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import { useIsMobile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { tModalComponentProps } from "src/redux/modals/types";
import { reportDateRanges, reportTypes } from "src/util/comboBoxData";
import apiNew, { getApiError } from "src/util/apiNew";
import { handleErrorEvents } from "src/redux/errorHandler/actions";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { PdSelect, tPdSelectOption } from "src/components/forms/select";
import { PdStack } from "src/components/layout";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { useAppDispatch } from "src/redux";
import { openUrlInNewTab } from "src/util/linking";
import { PdDateRangeInput } from "src/components/forms/input";

type tGetReportsExcelParams = { type: string; start?: string; end?: string };

type tModalParams = { reportType: { label: string; value: string }; period: [Date, Date] };

const getReportExcel = (params: tGetReportsExcelParams) =>
  apiNew
    .get<string>(`/reports/${params.type}/excel`, { params: { start: params.start, end: params.end } })
    .then((res) => res.data);

const ExportReportsModal: React.FC<tModalComponentProps<tModalParams>> = ({
  onResolve,
  onClose,
  reportType: reportTypeParam,
  period: periodParam,
}) => {
  const [reportType, setReportType] = React.useState(reportTypeParam);
  const [period, setPeriod] = React.useState(periodParam);
  const [dateRange, setDateRange] = React.useState(reportDateRanges[3]);
  const [isSubmitting, setSubmitting] = React.useState(false);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  const isCustom = dateRange.value === "Custom";

  const handleOnClose = () => {
    onClose();
  };

  const handleOnSelect = (option: tPdSelectOption | null) => {
    if (option) setReportType(option);
  };

  const handleOnSelectDateRange = (option: tPdSelectOption | null) => {
    if (option) setDateRange(option);
  };

  const handleOnChangePeriod = (dates: [Date, Date]) => {
    setPeriod(dates);
  };

  const handleOnExport = async () => {
    try {
      setSubmitting(true);

      const params: tGetReportsExcelParams = { type: reportType.value };
      if (dateRange.value === "Last 30 Days") {
        params.start = String(moment().subtract(30, "days").startOf("day").valueOf());
        params.end = String(moment().endOf("day").valueOf());
      } else if (dateRange.value === "Last 60 Days") {
        params.start = String(moment().subtract(60, "days").startOf("day").valueOf());
        params.end = String(moment().endOf("day").valueOf());
      } else if (dateRange.value === "Custom") {
        params.start = String(moment(period[0]).startOf("day").valueOf());
        params.end = String(moment(period[1]).endOf("day").valueOf());
      }

      const response = await getReportExcel(params);

      if (response) openUrlInNewTab(response);
      dispatch(handleErrorEvents(`Successfully Exported Report!`, "success"));
      onResolve();
    } catch (e) {
      setSubmitting(false);
      dispatch({ type: "RIBBON", message: new Error(getApiError(e).message) });
    }
  };

  return (
    <PdModal style={styles.modal} closeOnOverlayClick={!isSubmitting} onClose={onClose}>
      <PdModalCloseButton isDisabled={isSubmitting} onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdModalTitle>Export Report</PdModalTitle>
        <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} style={styles.modalFields}>
          <View style={styles.zIndex1}>
            <PdSelect value={reportType} onChange={handleOnSelect} options={reportTypes} isClearable={false} />
          </View>
          <View>
            <View style={styles.dateRangeField}>
              <PdSelect
                value={dateRange}
                onChange={handleOnSelectDateRange}
                options={reportDateRanges}
                isClearable={false}
              />
            </View>
            {isCustom && <PdDateRangeInput period={[period[0], period[1]]} onChange={handleOnChangePeriod} />}
          </View>
        </PdStack>
        <PdStack
          direction={isMobile ? "vertical" : "horizontal"}
          isReversed={isMobile}
          layout={[1, 1]}
          gap={3}
          style={styles.modalButtonGroup}
        >
          <PdButton colorScheme="purple" isDisabled={isSubmitting} onPress={handleOnClose} variant="outline">
            <PdButtonLabel>Cancel</PdButtonLabel>
          </PdButton>
          <PdButton isLoading={isSubmitting} onPress={handleOnExport}>
            <PdButtonLabel>Export</PdButtonLabel>
          </PdButton>
        </PdStack>
      </ScrollView>
    </PdModal>
  );
};

const useExportReportsModal = (): tUseModal<tModalParams> => {
  return useModal(ExportReportsModal);
};

export default useExportReportsModal;

const styles = StyleSheet.create({
  dateRangeField: {
    marginBottom: 2 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  modal: {
    marginHorizontal: 8 * kStyleConst.kSpacing,
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    alignItems: "flex-start",
    backgroundColor: kColors.lightBrand,
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  modalButtonGroup: {
    paddingTop: 40 * kStyleConst.kSpacing,
    width: "100%",
  },
  modalFields: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 8 * kStyleConst.kSpacing,
    width: "100%",
    zIndex: 1,
  },
  zIndex1: {
    zIndex: 1,
  },
});
